import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { CharacterMood } from '@/components/character';
import { useCharacter } from '@/contexts/CharacterContext';
import { useEffectiveChildMode } from '@/components/utils/useEffectiveChildMode';

/**
 * useCompanionMoment — deklarativt "ögonblick" för den inbäddade kompanjonen.
 *
 * Sätter mood + pratbubbeltext vid mount och NOLLSTÄLLER (mood='idle', tom text)
 * vid unmount. Mönstret är hämtat från DailyPage.tsx:27-37 men centraliserat så
 * att varje barn-vy bara behöver en rad:
 *
 *   useCompanionMoment('happy', 'child.companion.stars');
 *
 * speechKey översätts via i18next med valfria interpolationsvariabler (vars).
 * Effekten körs ENDAST i barnläge (effectiveChildMode) — i vuxenläge är det en
 * no-op, vilket håller invarianten "inget barn-läckage".
 *
 * Hooken är medvetet beroende av speechKey + en stringifierad vars-nyckel så att
 * texten uppdateras om vyn byter ögonblick (t.ex. wizard-steg) utan att fastna.
 */
export const useCompanionMoment = (
  mood: CharacterMood,
  speechKey: string,
  vars?: Record<string, string | number>,
): void => {
  const effectiveChildMode = useEffectiveChildMode();
  const { setMood, setCompanionSpeech } = useCharacter();
  const { t } = useTranslation();

  // Stabil dep för interpolationsvariabler (objekt-referensen ändras varje render).
  const varsKey = vars ? JSON.stringify(vars) : '';

  useEffect(() => {
    if (!effectiveChildMode) {
      return;
    }
    setMood(mood);
    setCompanionSpeech(t(speechKey, vars ?? {}) ?? '');
    return () => {
      setCompanionSpeech('');
      setMood('idle');
    };
    // varsKey speglar vars-innehållet; t/setMood/setCompanionSpeech är stabila.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveChildMode, mood, speechKey, varsKey, t, setMood, setCompanionSpeech]);
};
